import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { isNil, includes, forEach } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, of, Observable } from 'rxjs';
import { LocalStorageService } from './storage/local-storage.service';
import { Roles } from '../enums';
import { UserService } from './api/user.service';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { OverlayLoaderService } from './overlay-loader.service';
import { catchError, finalize } from 'rxjs/operators';
import { User, UserPermissions } from '../models/user';
import { NotificationDialogComponent } from '../dialog/notification-dialog/notification-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

  private _decodedJwt?: CurrentUser;
  public permissions: Record<string, any> = {};
  public readonly firstName$ = new BehaviorSubject<any>('');
  public readonly lastName$ = new BehaviorSubject<any>('');
  public permissionLoadComplete$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _localStorage: LocalStorageService,
    private cookieService: CookieService,
    private overlayLoaderService: OverlayLoaderService,
    private userService: UserService,
    private readonly matDialog: MatDialog,
  ) {}

  public applyMunicipalityRestrictionsAsObservable(currentMunicipality: MapStoreMunicipality): Observable<UserPermissions[]> {
    return this.userService.getUserPermissions(this.currentUser.guid, currentMunicipality.guid).pipe(
      catchError(() => {
        return of([]);
      }),
    );
  }

  applyMunicipalityRestrictions(municipalityId: string): void {
    this.permissionLoadComplete$.next(false);
    this.overlayLoaderService.isLoading = true;
    this.userService
      .getUserPermissions(this.currentUser.guid, municipalityId)
      .pipe(
        finalize(() => (this.overlayLoaderService.isLoading = false)),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe((p) => this.applyMunicipalityRestriction(p));
  }

  private applyMunicipalityRestriction(p: UserPermissions[]): void {
    this.permissions = {};
    forEach(p, (d) => {
      this.permissions[d.permissionCode] = { teaser: d.isTeaser };
    });
    this.permissions['METER_READING_INPUT'] = { teaser: true };
    this.permissionLoadComplete$.next(true);
  }

  private get decodedJwt(): any {
    if (this._decodedJwt) {
      return this._decodedJwt;
    }

    const token = this.jwt;
    if (token) {
      this._decodedJwt = jwtDecode(token);
      return this._decodedJwt;
    }

    return {};
  }

  updateCurrentUser(
    firstname?: string,
    lastname?: string,
    telefon?: string,
    mobile?: string,
    fax?: string,
    malFunctionNotificationStatus?: string,
    allowToReceiveNewDocumentsEmails?: boolean,
  ) {
    const user = this.decodedJwt;
    user.firstname = firstname;
    user.lastname = lastname;
    user.telefon = telefon;
    user.mobil = mobile;
    user.fax = fax;
    user.malFunctionNotificationStatus = malFunctionNotificationStatus;
    user.allowToReceiveNewDocumentsEmails = allowToReceiveNewDocumentsEmails;

    this._decodedJwt = user;
    this.updateName = user;
  }

  logout() {
    this.clearPostLoginRedirect();
    this._localStorage.clear();
    sessionStorage.clear();
    globalThis.location.href = `${globalThis.location.protocol}//${globalThis.location.host}/auth/logout`;
  }

  get currentUser(): CurrentUser {
    const user = this.decodedJwt;

    if (user.guid) {
      return user;
    }

    if (!globalThis.location.pathname.includes('public')) {
      this.login(true);
      return {} as CurrentUser;
    }

    return {} as CurrentUser;
  }

  get appToken(): string {
    return this.cookieService.get('id_token');
  }

  get jwt(): string {
    return this.cookieService.get('id_token');
  }

  get idpToken(): string {
    return this.cookieService.get('access_token');
  }

  get sessionId(): string {
    return this.decodedJwt.sessionId || '';
  }

  get isSystemAdmin(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.systemAdministrator], this.currentUser.roleCode);
  }

  get isKAM(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.keyAccountManager], this.currentUser.roleCode);
  }

  get isBackOffice(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.backOffice], this.currentUser.roleCode);
  }

  get isPOHolding(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.poHolding], this.currentUser.roleCode);
  }

  get isAdminAreaAndMunicipalityWorker(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.administrationAreaWorker, Roles.municipalityWorker], this.currentUser.roleCode);
  }

  get isMunicipalityWorker(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.municipalityWorker], this.currentUser.roleCode);
  }

  get isDsoUser(): boolean {
    if (isNil(this.currentUser)) {
      return false;
    }

    return includes([Roles.technicalDsoExpert, Roles.technicalDsoEmployee], this.currentUser.roleCode);
  }

  login(includeRelayState = false) {
    if (includeRelayState) {
      this.storeCurrentUrlForPostLoginRedirect();
    }

    const currentUrl = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash || ''}`;
    const hostNameAndPath = `${globalThis.location.host}/auth/login`;
    const relayState = `?relayState=${encodeURIComponent(currentUrl)}`;

    globalThis.location.href = `${globalThis.location.protocol}//${hostNameAndPath}${includeRelayState ? relayState : ''}`;
  }

  public restorePostLoginRedirectIfNeeded(): void {
    const targetUrl = sessionStorage.getItem(AuthService.POST_LOGIN_REDIRECT_KEY);

    if (!targetUrl) {
      return;
    }

    // only restore after authentication is actually complete
    if (!(this.appToken || '').trim()) {
      return;
    }

    const currentUrl = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash || ''}`;

    if (currentUrl.includes('relayState')) {
      return;
    }

    // if user already landed on the intended target, just clear stale storage
    if (currentUrl === targetUrl) {
      this.clearPostLoginRedirect();
      return;
    }

    // only auto-restore when app lands on a default post-login page
    const isDefaultLandingPage =
      currentUrl === '/' ||
      currentUrl === '' ||
      currentUrl.startsWith('/instrumententafel') ||
      currentUrl.startsWith('/ciamcallback') ||
      currentUrl.startsWith('/aadcallback');

    if (!isDefaultLandingPage) {
      return;
    }

    this.clearPostLoginRedirect();
    globalThis.location.replace(targetUrl);
  }

  private storeCurrentUrlForPostLoginRedirect(): void {
    const currentUrl = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash || ''}`;

    if (!this.shouldStorePostLoginRedirect(currentUrl)) {
      return;
    }

    sessionStorage.setItem(AuthService.POST_LOGIN_REDIRECT_KEY, currentUrl);
  }

  private shouldStorePostLoginRedirect(url: string): boolean {
    if (!url || url === '/') {
      return false;
    }

    const excludedPrefixes = ['/auth', '/public', '/error', '/ciamcallback', '/aadcallback'];
    return !excludedPrefixes.some((prefix) => url.startsWith(prefix));
  }

  private clearPostLoginRedirect(): void {
    sessionStorage.removeItem(AuthService.POST_LOGIN_REDIRECT_KEY);
  }

  public set updateName(user: CurrentUser) {
    this.firstName$.next(user.firstname);
    this.lastName$.next(user.lastname);
  }

  public sessionExpiredModal(): void {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
          orientation: 'row',
          alignment: 'start',
        },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe(() => this.logout());
  }
}

export interface CurrentUser extends User {
  imageSrc: any;
  sessionId: string;
  roleName: string;
  roleCode: string;
  managedRoles: string;
  lastLogin: number;
}
